/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useAuth } from "../auth";
import { useFirebase } from "../Firebase";
import { getObjFromLink, transformCollectionSnap } from "../helpers";
import { useKegs } from "../kegs";
import { formatBatchData } from "./formatting";

const isDev = process.env.NODE_ENV === "development";

const BatchesContext = createContext({});

export const BatchesProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims, companyLink: authCompanyLink } = useAuth();
  const { companyKegs, partnerKegs } = useKegs();

  const companyLink = authCompanyLink || claims?.companyLink;

  const [batches, setBatches] = useState();

  const dateIsValid = useCallback(date => {
    return !Number.isNaN(new Date(date).getTime());
  }, []);

  useEffect(() => {
    if (isDev) {
      console.log("[BatchesProvider] Setting up batches listener:", {
        companyLink,
        hasCompanyLink: !!companyLink,
      });
    }

    return companyLink
      ? firebase.batches(getObjFromLink(companyLink).id).onSnapshot(
          snap => {
            if (isDev) {
              console.log("[BatchesProvider] Batches snapshot received:", {
                count: snap.size,
              });
            }

            setBatches(
              transformCollectionSnap(snap)
                .filter(batch => batch.bestBefore !== "Invalid Date")
                .filter(batch => batch.bestBefore !== undefined)
                .map(batch => ({
                  ...batch,
                  bestBefore: new Date(
                    dateIsValid(batch.bestBefore)
                      ? batch.bestBefore
                      : batch.bestBefore.toDate()
                  ),
                  dateCreated: new Date(
                    dateIsValid(batch.dateCreated)
                      ? batch.dateCreated
                      : batch.dateCreated.toDate()
                  ),
                }))
                .sort((a, b) => {
                  const firstDate = a.dateCreated;
                  const secDate = b.dateCreated;

                  if (firstDate > secDate) return 1;
                  if (firstDate < secDate) return -1;
                  return 0;
                })
            );
          },
          err => {
            if (isDev) {
              console.error("[BatchesProvider] Batches snapshot error:", err);
            }

            console.log(err);
          }
        )
      : () => {
          if (isDev) {
            console.log(
              "[BatchesProvider] No companyLink, skipping batches listener"
            );
          }
        };
  }, [
    firebase,
    dateIsValid,
    companyLink,
  ]);

  const createBatch = async batch => {
    if (!companyLink) {
      if (isDev) {
        console.error("[BatchesProvider] Cannot create batch - no companyLink");
      }

      return;
    }

    const newBatch = formatBatchData(batch, companyLink, true);

    return await firebase.batches(getObjFromLink(companyLink).id).add(newBatch);
  };

  const updateBatch = useCallback(
    async (batch, id) => {
      if (!companyLink) {
        if (isDev) {
          console.error(
            "[BatchesProvider] Cannot update batch - no companyLink"
          );
        }

        return;
      }

      const updatedBatch = formatBatchData(batch, companyLink);
      const fsBatch = firebase.db.batch();

      const batchDocRef = firebase
        .batches(getObjFromLink(companyLink).id)
        .doc(id);

      await fsBatch.update(batchDocRef, updatedBatch);

      if (companyKegs && partnerKegs) {
        const newBatchData = {
          batch: {
            batchNo: updatedBatch.batchNo,
            bestBefore: updatedBatch.bestBefore,
          },
        };

        const kegs = [
          ...companyKegs,
          ...partnerKegs,
        ];

        const brandedKegs = kegs.filter(k => k.batch?.id === id);

        brandedKegs.forEach(
          async k =>
            await fsBatch.update(
              firebase.db.collection("kegs").doc(k.id),
              newBatchData
            )
        );
      }

      await fsBatch.commit();
    },
    [
      companyLink,
      companyKegs,
      firebase,
      partnerKegs,
    ]
  );

  const archiveBatch = useCallback(
    async (isArchive, id) => {
      if (!companyLink) {
        if (isDev) {
          console.error(
            "[BatchesProvider] Cannot archive batch - no companyLink"
          );
        }

        return;
      }

      return await firebase
        .batches(getObjFromLink(companyLink).id)
        .doc(id)
        .update({
          dateUpdated: new Date(),
          isArchive: !isArchive,
        });
    },
    [
      companyLink,
      firebase,
    ]
  );
  return (
    <BatchesContext.Provider
      value={{
        archiveBatch,
        batches,
        createBatch,
        updateBatch,
      }}
    >
      {children}
    </BatchesContext.Provider>
  );
};

export const BatchesConsumer = BatchesContext.Consumer;

export const useBatches = () => useContext(BatchesContext);
